'use client';

import { useAuth } from '@clerk/clerk-react';
import { useQuery } from '@tanstack/react-query';

import { apiClient } from '../api/client';

interface Pricing {
  memberCount: number | null;
  pricePerMemberCents: number;
  subtotalCents: number | null;
  taxCents: number | null;
  totalCents: number | null;
  currency: string;
  trialEndedAt: string | null;
  inputTokenPricePerMillionCents: number;
  outputTokenPricePerMillionCents: number;
  inputTokensPerSeatPerMonth: number;
  outputTokensPerSeatPerMonth: number;
  minimumInputTokens: number;
  minimumOutputTokens: number;
  currentPricePerSeatCents: number | null;
  nextPricePerSeatCents: number | null;
  maxPricePerSeatCents: number | null;
  seatsRemainingAtCurrentPrice: number | null;
  totalSeatsAtCurrentPrice: number | null;
  tierLabel: string | null;
}

const DEFAULT_INPUT_TOKEN_PRICE_PER_MILLION_CENTS = 500;
const DEFAULT_OUTPUT_TOKEN_PRICE_PER_MILLION_CENTS = 2000;
const DEFAULT_INPUT_TOKENS_PER_SEAT_PER_MONTH = 3_000_000;
const DEFAULT_OUTPUT_TOKENS_PER_SEAT_PER_MONTH = 1_000_000;
const DEFAULT_MINIMUM_INPUT_TOKENS = 1_000_000;
const DEFAULT_MINIMUM_OUTPUT_TOKENS = 1_000_000;

/**
 * Hook for fetching pricing data
 * GET /api/v1/billing/pricing
 *
 * Returns pricing calculations with flat fields at top level
 */
export function usePricing() {
  const {
    getToken,
    isLoaded,
    isSignedIn,
    orgId: clerkOrganizationId,
  } = useAuth();

  return useQuery<Pricing>({
    queryKey: ['pricing', clerkOrganizationId],
    enabled: isLoaded && isSignedIn === true && !!clerkOrganizationId,
    queryFn: async () => {
      const token = await getToken();
      if (!token) {
        throw new Error('No authentication token available');
      }

      const { data, error, response } = await apiClient.GET(
        '/api/v1/billing/pricing',
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (error || !response.ok || !data) {
        throw new Error('Failed to fetch pricing data');
      }

      return {
        memberCount: data.memberCount ?? null,
        pricePerMemberCents: data.pricePerMemberCents,
        subtotalCents: data.subtotalCents ?? null,
        taxCents: data.taxCents ?? null,
        totalCents: data.totalCents ?? null,
        currency: data.currency,
        trialEndedAt: data.trialEndedAt ?? null,
        inputTokenPricePerMillionCents:
          data.inputTokenPricePerMillionCents ??
          DEFAULT_INPUT_TOKEN_PRICE_PER_MILLION_CENTS,
        outputTokenPricePerMillionCents:
          data.outputTokenPricePerMillionCents ??
          DEFAULT_OUTPUT_TOKEN_PRICE_PER_MILLION_CENTS,
        inputTokensPerSeatPerMonth:
          data.inputTokensPerSeatPerMonth ??
          DEFAULT_INPUT_TOKENS_PER_SEAT_PER_MONTH,
        outputTokensPerSeatPerMonth:
          data.outputTokensPerSeatPerMonth ??
          DEFAULT_OUTPUT_TOKENS_PER_SEAT_PER_MONTH,
        minimumInputTokens:
          data.minimumInputTokens ?? DEFAULT_MINIMUM_INPUT_TOKENS,
        minimumOutputTokens:
          data.minimumOutputTokens ?? DEFAULT_MINIMUM_OUTPUT_TOKENS,
        currentPricePerSeatCents: data.currentPricePerSeatCents ?? null,
        nextPricePerSeatCents: data.nextPricePerSeatCents ?? null,
        maxPricePerSeatCents: data.maxPricePerSeatCents ?? null,
        seatsRemainingAtCurrentPrice: data.seatsRemainingAtCurrentPrice ?? null,
        totalSeatsAtCurrentPrice: data.totalSeatsAtCurrentPrice ?? null,
        tierLabel: data.tierLabel ?? null,
      };
    },
    staleTime: 30000,
    refetchInterval: 60000,
  });
}
